import type { AreaUnit, LengthUnit, UnitSystem } from '../../store/slices/measureSlice.js';

/**
 * Turning meters into something a person reads.
 *
 * The geodetic operators are asked for meters and nothing else, so every
 * conversion in the application happens here, against exact constants that are
 * under test. That keeps one canonical number in the store, makes a unit change
 * a pure re-render rather than a round trip to the SDK, and means the factors
 * can be checked against a reference rather than trusted.
 *
 * The anchor worth remembering: a PLSS section is one square mile, which is
 * 2,589,988.110336 square meters, which is exactly 640 acres. Sections are one
 * of the two layers this client leads with, so that is the number a demo will
 * be checked against.
 *
 * Chains are offered because the PLSS was surveyed in them - a section line is
 * 80 chains, a quarter-quarter is 20 - and anyone working section corners in
 * Oklahoma thinks in them.
 *
 * The US survey foot is deliberately not offered. It differs from the
 * international foot by 2.0 parts per million, which is 0.011 feet on a
 * one-mile line and below every precision displayed here. Listing both would
 * imply a distinction these numbers cannot carry.
 */

/** Exact by definition. The international foot is 0.3048 m exactly. */
export const METERS_PER: Readonly<Record<LengthUnit, number>> = {
  feet: 0.3048,
  chains: 20.1168,
  miles: 1609.344,
  meters: 1,
  kilometers: 1000,
};

export const SQUARE_METERS_PER: Readonly<Record<AreaUnit, number>> = {
  'square-feet': 0.09290304,
  acres: 4046.8564224,
  'square-miles': 2589988.110336,
  hectares: 10_000,
  'square-kilometers': 1e6,
};

/** Abbreviation for the readout, and the full word for the live region. */
interface UnitNames {
  readonly short: string;
  readonly spoken: string;
  readonly spokenSingular: string;
  /** Digits after the decimal point, chosen so the display is not wider than the measurement is good. */
  readonly decimals: number;
}

const LENGTH_NAMES: Readonly<Record<LengthUnit, UnitNames>> = {
  feet: { short: 'ft', spoken: 'feet', spokenSingular: 'foot', decimals: 0 },
  chains: { short: 'ch', spoken: 'chains', spokenSingular: 'chain', decimals: 2 },
  miles: { short: 'mi', spoken: 'miles', spokenSingular: 'mile', decimals: 2 },
  meters: { short: 'm', spoken: 'meters', spokenSingular: 'meter', decimals: 0 },
  kilometers: { short: 'km', spoken: 'kilometers', spokenSingular: 'kilometer', decimals: 2 },
};

const AREA_NAMES: Readonly<Record<AreaUnit, UnitNames>> = {
  'square-feet': { short: 'sq ft', spoken: 'square feet', spokenSingular: 'square foot', decimals: 0 },
  acres: { short: 'ac', spoken: 'acres', spokenSingular: 'acre', decimals: 2 },
  'square-miles': { short: 'sq mi', spoken: 'square miles', spokenSingular: 'square mile', decimals: 3 },
  hectares: { short: 'ha', spoken: 'hectares', spokenSingular: 'hectare', decimals: 2 },
  'square-kilometers': {
    short: 'km²',
    spoken: 'square kilometers',
    spokenSingular: 'square kilometer',
    decimals: 2,
  },
};

/** Units offered in each system, in the order they appear in the picker. */
export const LENGTH_UNITS: Readonly<Record<UnitSystem, readonly LengthUnit[]>> = {
  us: ['feet', 'chains', 'miles'],
  metric: ['meters', 'kilometers'],
};

export const AREA_UNITS: Readonly<Record<UnitSystem, readonly AreaUnit[]>> = {
  us: ['square-feet', 'acres', 'square-miles'],
  metric: ['hectares', 'square-kilometers'],
};

export interface Formatted {
  /** Grouped and rounded, without the unit. */
  readonly value: string;
  /** Abbreviation, for the readout. */
  readonly unit: string;
  /** "1.00 miles", for an aria-live region, where "1.00 mi" reads poorly. */
  readonly spoken: string;
  /** The unit the value is actually in, after automatic selection. */
  readonly chosen: LengthUnit | AreaUnit;
}

export const LENGTH_UNIT_LABELS: Readonly<Record<LengthUnit, string>> = {
  feet: 'Feet',
  chains: 'Chains',
  miles: 'Miles',
  meters: 'Meters',
  kilometers: 'Kilometers',
};

export const AREA_UNIT_LABELS: Readonly<Record<AreaUnit, string>> = {
  'square-feet': 'Square feet',
  acres: 'Acres',
  'square-miles': 'Square miles',
  hectares: 'Hectares',
  'square-kilometers': 'Square kilometers',
};

/**
 * The unit automatic mode picks, by magnitude.
 *
 * US length switches at one mile rather than at some round number of feet, so a
 * section line reads as a mile rather than as 5,280 feet - and the secondary
 * readout shows the feet anyway, so nothing is hidden. US area switches at an
 * acre and again at 640 acres, which is one section. Metric area switches once,
 * at a square kilometer, which is 100 hectares.
 */
export function autoLengthUnit(meters: number, system: UnitSystem): LengthUnit {
  const m = Math.abs(meters);
  if (system === 'metric') return m < METERS_PER.kilometers ? 'meters' : 'kilometers';
  return m < METERS_PER.miles ? 'feet' : 'miles';
}

export function autoAreaUnit(squareMeters: number, system: UnitSystem): AreaUnit {
  const a = Math.abs(squareMeters);
  if (system === 'metric') {
    return a < SQUARE_METERS_PER['square-kilometers'] ? 'hectares' : 'square-kilometers';
  }
  if (a < SQUARE_METERS_PER.acres) return 'square-feet';
  return a < SQUARE_METERS_PER['square-miles'] ? 'acres' : 'square-miles';
}

/**
 * A second reading in the unit the automatic threshold did not pick, so a
 * choice between "1.00 mi" and "5,280 ft" never costs the user the other one.
 * Returns null when there is nothing useful to add.
 */
export function secondaryLengthUnit(chosen: LengthUnit): LengthUnit | null {
  if (chosen === 'miles') return 'feet';
  if (chosen === 'kilometers') return 'meters';
  return null;
}

export function secondaryAreaUnit(chosen: AreaUnit): AreaUnit | null {
  if (chosen === 'square-miles') return 'acres';
  if (chosen === 'square-kilometers') return 'hectares';
  if (chosen === 'square-feet') return null;
  return null;
}

function group(value: number, decimals: number): string {
  // Rounded before formatting so that a tiny negative - which a geodesic
  // result can be, a hair either side of zero - collapses to 0 rather than
  // displaying as "-0", which reads as a bug.
  const rounded = Number(value.toFixed(decimals));
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(rounded === 0 ? 0 : rounded);
}

function format(value: number, names: UnitNames, chosen: LengthUnit | AreaUnit): Formatted {
  const text = group(value, names.decimals);
  // "1 foot" rather than "1 feet"; the rounded text is what decides, so 1.004
  // displayed as "1.00" is spoken as singular too.
  const singular = Math.abs(Number(text.replace(/,/g, ''))) === 1;
  return {
    value: text,
    unit: names.short,
    spoken: `${text} ${singular ? names.spokenSingular : names.spoken}`,
    chosen,
  };
}

export function formatLength(
  meters: number,
  system: UnitSystem,
  unit: LengthUnit | 'auto',
): Formatted {
  const chosen = unit === 'auto' ? autoLengthUnit(meters, system) : unit;
  return format(meters / METERS_PER[chosen], LENGTH_NAMES[chosen], chosen);
}

export function formatArea(
  squareMeters: number,
  system: UnitSystem,
  unit: AreaUnit | 'auto',
): Formatted {
  const chosen = unit === 'auto' ? autoAreaUnit(squareMeters, system) : unit;
  return format(squareMeters / SQUARE_METERS_PER[chosen], AREA_NAMES[chosen], chosen);
}
